import { useMemo } from 'react';
import { Box, Typography, Stack, Chip, Table, TableBody, TableCell, TableRow, TableHead } from '@mui/material';
import { extractValues, fTest, tTest, mannWhitneyU } from '../statistics';
import { useTimelineControls } from '../TimelineContext';

export default function StatTestingWidget({ recordings = [], widgetConfig }) {
  const { window: timeWindow } = useTimelineControls();
  const recId1 = widgetConfig?.recId1 || '';
  const recId2 = widgetConfig?.recId2 || '';
  const sensorKey = widgetConfig?.sensorKey || '';

  const rec1 = recordings.find((r) => r.id === recId1);
  const rec2 = recordings.find((r) => r.id === recId2);

  const results = useMemo(() => {
    if (!rec1 || !rec2 || !sensorKey) return null;
    const vals1 = extractValues(rec1, sensorKey, timeWindow?.start, timeWindow?.end);
    const vals2 = extractValues(rec2, sensorKey, timeWindow?.start, timeWindow?.end);
    if (vals1.length < 2 || vals2.length < 2) return null;

    const fResult = fTest(vals1, vals2);
    const equalVar = fResult.result === 'Equal Variance';
    const tResult = tTest(vals1, vals2, equalVar);
    const mwResult = mannWhitneyU(vals1, vals2);

    return { fResult, tResult, mwResult, n1: vals1.length, n2: vals2.length };
  }, [rec1, rec2, sensorKey, timeWindow]);

  const sigColor = (result) => result === 'Significant' ? 'error.main' : 'text.secondary';
  const fmtP = (p) => isNaN(p) ? '—' : p < 0.001 ? '<0.001' : p.toFixed(4);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {results ? (
        <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
            <Chip label={`n₁=${results.n1}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
            <Chip label={`n₂=${results.n2}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
            {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }}>Test</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">Statistic</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">p-value</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>F-Test (variance)</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">{results.fResult.fStatistic.toFixed(4)}</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">{fmtP(results.fResult.pValue)}</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', color: results.fResult.result === 'Unequal Variance' ? 'warning.main' : 'text.secondary' }} align="right">
                  {results.fResult.result}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>T-Test ({results.tResult.variant})</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">{results.tResult.tStatistic.toFixed(4)}</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">{fmtP(results.tResult.pValue)}</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold', color: sigColor(results.tResult.result) }} align="right">
                  {results.tResult.result}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>Mann-Whitney U</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">U={results.mwResult.u.toFixed(0)}</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">{fmtP(results.mwResult.pValue)}</TableCell>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold', color: sigColor(results.mwResult.result) }} align="right">
                  {results.mwResult.result}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select two recordings and a sensor to test
          </Typography>
        </Box>
      )}
    </Box>
  );
}
